import {
  Color,
  DoubleSide,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector2,
} from 'three';
import { COLORS } from '../config/colors';

export class WaterSystem {
  readonly mesh: Mesh<PlaneGeometry, ShaderMaterial>;
  private readonly uniforms: Record<string, { value: unknown }>;

  constructor(mobile: boolean) {
    const segments = mobile ? 42 : 96;
    const geometry = new PlaneGeometry(160, 24, segments, mobile ? 14 : 32);
    this.uniforms = {
      uTime: { value: 0 },
      uSpeed: { value: 1 },
      uShallow: { value: new Color(COLORS.river) },
      uDeep: { value: new Color(COLORS.riverDeep) },
      uResolution: { value: new Vector2(window.innerWidth, window.innerHeight) },
    };
    const material = new ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      opacity: 0.9,
      side: DoubleSide,
      depthWrite: false,
      vertexShader: `
        uniform float uTime;
        uniform float uSpeed;
        varying vec2 vUv;
        varying float vWave;
        void main() {
          vUv = uv;
          vec3 p = position;
          float flow = uTime * (0.5 + uSpeed * 0.12);
          float waveA = sin(p.x * 0.48 + flow * 2.4 + p.y * 0.2) * 0.11;
          float waveB = cos(p.y * 1.4 - flow + p.x * 0.12) * 0.08;
          float edge = sin(vUv.y * 3.1415926);
          p.z += (waveA + waveB) * edge;
          vWave = waveA + waveB;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec3 uShallow;
        uniform vec3 uDeep;
        varying vec2 vUv;
        varying float vWave;
        void main() {
          float depth = smoothstep(0.0, 0.48, min(vUv.y, 1.0 - vUv.y));
          vec3 color = mix(uShallow, uDeep, depth * 0.82);
          float stripe = sin((vUv.x * 62.0 - uTime * 2.5) + sin(vUv.y * 16.0)) * 0.5 + 0.5;
          color += stripe * 0.045 + vWave * 0.16;
          float alpha = 0.78 + depth * 0.16;
          gl_FragColor = vec4(color, alpha);
        }
      `,
    });
    this.mesh = new Mesh(geometry, material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.y = 0.2;
    this.mesh.receiveShadow = true;
    this.mesh.name = 'FlowingRiver';
  }

  update(time: number, speed: number): void {
    this.uniforms.uTime.value = time;
    this.uniforms.uSpeed.value = speed;
  }

  resize(width: number, height: number): void {
    (this.uniforms.uResolution.value as Vector2).set(width, height);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
